import React, { useMemo } from 'react';
import { Button, Modal, SortableTh, sortRows, useLastPresent, useTableSort } from '@nsi-advisory-services/components';

import { getCategoryLabel } from '../../utils/expenseCategories';

// What made up one employee's reimbursement figure for the period.
//
// The sheet shows a single total; this is where that total is accounted for.
// It reads from the run payload rather than re-querying expenses, so a saved
// run shows the expenses it was filed with even if one has since been edited.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Opens one expense on the Approvals page, in a new tab so the pay run stays
// where it was. The ?expense= parameter is what makes that work whatever the
// inbox's filters happen to be set to - the list here spans approved, unpaid
// and pending, so no single default view would be showing all of them.
const openInApprovals = (expenseId) => {
  window.open(`/approvals?expense=${encodeURIComponent(expenseId)}`, '_blank', 'noopener,noreferrer');
};

// What each bucket is called, and how its chip reads. `stage` is decided in SQL
// - see loadReimbursements in routes/payroll.js.
const STAGE = {
  paid: { label: 'Paid', tone: 'paid' },
  unpaid: { label: 'Unpaid', tone: 'unpaid' },
  pending: { label: 'Awaiting approval', tone: 'pending' },
};

const stageLabel = (item) => (STAGE[item.stage] || STAGE.paid).label;

const COLUMNS = [
  { key: 'status', label: 'Status' },
  { key: 'paidOn', label: 'Paid' },
  { key: 'category', label: 'Category' },
  { key: 'description', label: 'Description' },
  { key: 'amount', label: 'Amount', align: 'right' },
];

// Sorted on what the cell shows, not on what is stored: the reader is sorting a
// column they can see. Status therefore sorts by its label, which puts
// "Awaiting approval" ahead of "Paid" and "Unpaid" - the default, and the useful
// one, because the rows that need somebody to act come first.
const SORT_ACCESSORS = {
  status: stageLabel,
  paidOn: (item) => item.paidOn || '',
  category: (item) => getCategoryLabel(item.category),
  description: (item) => item.description || '',
  amount: (item) => Number(item.amount) || 0,
};

// Rendered inside the dialog, so it mounts fresh each time the dialog opens and
// the sort is back to status, A first, every time. A default is only a default if
// it is applied each time, including for a different person.
function ReimbursementsTable({ items, owed, owedTotal, pending, pendingTotal }) {
  const sort = useTableSort('status', 'asc');

  const sorted = useMemo(
    () => sortRows(items, sort.sortKey, sort.sortDir, SORT_ACCESSORS),
    [items, sort.sortKey, sort.sortDir]
  );

  return (
    <div className="nsi-table-wrap">
      <table className="nsi-table payroll-reimb__table">
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <SortableTh key={column.key} sort={sort} sortKey={column.key} align={column.align}>
                {column.label}
              </SortableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="nsi-table__empty">No expenses owed or in the queue.</td>
            </tr>
          ) : sorted.map((item) => (
            <tr
              key={item.id}
              className="nsi-table__row payroll-reimb__row"
              role="button"
              tabIndex={0}
              title="Open this expense in Approvals"
              onClick={() => openInApprovals(item.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openInApprovals(item.id);
                }
              }}
            >
              <td>
                <span className={`payroll-reimb__chip payroll-reimb__chip--${(STAGE[item.stage] || STAGE.paid).tone}`}>
                  {(STAGE[item.stage] || STAGE.paid).label}
                </span>
              </td>
              <td className="bdc-num">{fmtDate(item.paidOn)}</td>
              {/* The stored value is a slug - technology_reimbursement -
                  and the shared helper is what turns it into the name the
                  rest of the app shows, legacy values included. */}
              <td className="bdc-table-wrap">{getCategoryLabel(item.category) || '—'}</td>
              <td className="bdc-table-wrap">{item.description || '—'}</td>
              <td className="nsi-table__right bdc-num">
                {fmtMoney(item.amount)}
                <i className="bi bi-box-arrow-up-right payroll-reimb__go" aria-hidden="true" />
              </td>
            </tr>
          ))}
        </tbody>
        {items.length > 0 ? (
          <tfoot>
            <tr>
              <td colSpan={4}>
                Owed on this run &middot; {owed.length} expense{owed.length === 1 ? '' : 's'}
              </td>
              <td className="nsi-table__right bdc-num">{fmtMoney(owedTotal)}</td>
            </tr>
            {pending.length > 0 ? (
              <tr className="payroll-reimb__pendingfoot">
                <td colSpan={4}>
                  Awaiting approval &middot; {pending.length} expense{pending.length === 1 ? '' : 's'}
                  {' '}&middot; not included above
                </td>
                <td className="nsi-table__right bdc-num">{fmtMoney(pendingTotal)}</td>
              </tr>
            ) : null}
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

export default function PayrollReimbursementsModal({
  open,
  employee: employeeProp,
  period,
  onClose,
  // Set only when this is a saved run whose live expenses have moved on. Lets
  // the sync be taken from here as well as from the sheet - this dialog is
  // where somebody notices a figure is out of date.
  drift = null,
  onSync,
  syncing = false,
}) {
  const employee = useLastPresent(employeeProp); // keeps the dialog steady while it fades out

  // Memoised because the `|| []` fallback is a fresh array on every render, and
  // the totals below depend on it.
  const items = useMemo(() => employee?.reimbursementItems || [], [employee?.reimbursementItems]);

  // Split rather than summed straight through: a pending claim is not owed, so
  // it must not land in the same total as the approved ones. It is still listed
  // - the point of showing it is that somebody acts on it.
  const owed = items.filter((item) => item.stage !== 'pending');
  const pending = items.filter((item) => item.stage === 'pending');
  const sum = (rows) => rows.reduce((total, item) => total + (Number(item.amount) || 0), 0);
  const owedTotal = sum(owed);
  const pendingTotal = sum(pending);

  // Whether this particular person is one of the people whose expenses moved.
  // Named so the banner can say "these figures" rather than something vaguer
  // when it is their row that is stale.
  const driftForEmployee = (drift?.employees || []).find(
    (row) => String(row.employeeId) === String(employee?.employeeId)
  );

  return (
    <Modal open={open} onClose={onClose} size="md" overflow>
      <Modal.Header>
        <Modal.Title>Reimbursements &mdash; {employee?.name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted" style={{ fontSize: 13, marginTop: 0 }}>
          Approved expenses paid to {employee?.name} between{' '}
          <strong>{fmtDate(period?.start)}</strong> and <strong>{fmtDate(period?.end)}</strong>,
          plus anything approved and still unpaid whatever its date, and anything
          still awaiting a decision. Only the first two are owed on this run.
        </p>

        {/* The list below is what the run was filed with. If expenses have moved
            since, saying so here - where somebody is reading the figures line by
            line - is more use than making them go back to the sheet for it. */}
        {drift?.changed && onSync ? (
          <div className="payroll-reimb__driftbar">
            <span>
              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{' '}
              {driftForEmployee
                ? `These expenses have changed since the run was filed (${fmtMoney(driftForEmployee.before.amount)} → ${fmtMoney(driftForEmployee.after.amount)}).`
                : 'Expenses elsewhere on this run have changed since it was filed.'}
            </span>
            <Button size="sm" variant="primary" onClick={onSync} loading={syncing}>
              Sync now
            </Button>
          </div>
        ) : null}

        <ReimbursementsTable
          items={items}
          owed={owed}
          owedTotal={owedTotal}
          pending={pending}
          pendingTotal={pendingTotal}
        />
      </Modal.Body>
    </Modal>
  );
}
